import { useEffect, useState, useRef } from "react";
import { AutocompleteLocation } from "./AutocompleteLocation";
import { useNavigate } from "react-router-dom";
import { CompanySearch } from "./CompanySearch";
import { FavoritesTabs } from "./FavoritesTabs";
import "../styles/FiltersSection.css";
import * as Sentry from '@sentry/react';

function TitleInfoTooltip({ visible, onPin, onHoverEnter, onHoverLeave, selectedTitles }) {
  return (
    <span className="title-info-container">
      <button
        className="title-info-btn"
        aria-label="Search titles information"
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
        onClick={(e) => { e.stopPropagation(); onPin(); }}
      >
        ?
      </button>
      {visible && (
        <div
          className="title-tooltip-box"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
        >
          <p>These are your search titles. Click or unclick them to specify which titles you want to search for.</p>
          <p className="title-tooltip-subheading">Showing results for the following search titles:</p>
          {selectedTitles.length > 0 ? (
            <ul className="title-tooltip-list">
              {selectedTitles.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          ) : (
            <p className="title-tooltip-all">All titles seen below</p>
          )}
        </div>
      )}
    </span>
  );
}

function FiltersSection({
  user = {}, //User object
  distinct_titles = [],
  jobs = [], // All jobs for company search
  setUser = () => {}, //Function to update User object, usually for short frontend updates (i.e. changing selected filter)
  refreshJobs = (newJobs, newUser, newTotJobs) => {}, //Function to refresh jobs listings and user after update filter call, prevents whole page refresh
  setFirstWait = () => {}, //Set loading status
  firstWait = false,
  setPremiumPopup = () => {}, //Show premium popup
  setErrorMessage = () => {}, //Set error message
  setShowErrorPopup = () => {}, //Show the error popup
  selectedJobType = "",
  filtersDisabled = false,
  setFiltersDisabled = () => {},
  selectedCompanyIds = [], // Changed to array for multi-select
  setSelectedCompanyIds = () => {},
  favoriteCompanyIds = [],
  favoriteCompanyInfo = [], // Array of information for favorited companies
  onToggleFavoriteCompany = () => {},
  favoritesSubTab = "jobs",
  setFavoritesSubTab = () => {},
  onLoadCompanyJobs = () => {}, // Function to load jobs from selected companies
  onLoadFavoriteCompanyJobs = () => {}, // Function to load jobs from favorite companies
  allCompanies = [], // List of all companies for company search autocomplete
  companiesLoading = false, // Loading state for companies list
  onRefreshCompanies = () => {} // Refresh all companies list
}) {
  const navigate = useNavigate();
  const [addRec, setAddRec] = useState(false); //add title input
  const [filtersBox, setFiltersBox] = useState(false); //filter section open
  const [titleTooltipPinned, setTitleTooltipPinned] = useState(false);
  const [titleTooltipHovered, setTitleTooltipHovered] = useState(false);
  const titleTooltipVisible = titleTooltipPinned || titleTooltipHovered;
  const [locationBox, setLocationBox] = useState(false); //location box open
  const [sizesBox, setSizesBox] = useState(
    user.filter &&
      user.filters["selected_sizes"] &&
      user.filters["selected_sizes"].length > 0
  ); //sizes box open
  const [deletedTitles, setDeletedTitles] = useState([]); //deleted titles, for some reason react doesn't update mappings
  const [currentLocation, setCurrentLocation] = useState(
    user.filters?.location || ""
  );
  const [selectedSizes, setSelectedSizes] = useState(
    user.filters?.selected_sizes || []
  );
  const [serverDistinctTitles, setServerDistinctTitles] = useState(
    distinct_titles || []
  );
  const [locationIsValid, setLocationIsValid] = useState(false);

  const [localFilterType, setLocalFilterType] = useState(user.filters?.type || "All");
  const [localFilterInternational, setLocalFilterInternational] = useState(!!user.filters?.international_only);
  const [localFilterLocationType, setLocalFilterLocationType] = useState(user.filters?.location ? "Specified" : "None");
  const [localFilterSizes, setLocalFilterSizes] = useState((user.filters?.selected_sizes?.length ?? 0) > 0 ? "Selected" : "All");

  useEffect(() => {
    setServerDistinctTitles(distinct_titles || []);
  }, [distinct_titles]);

  useEffect(() => {
    //on loading of user
    setLocationBox(user.filters && user.filters["location"] !== null);
    setSizesBox(
      user.filters &&
        user.filters["selected_sizes"] &&
        user.filters["selected_sizes"].length > 0
    );
    setCurrentLocation(user.filters?.location || "");
    if (user.filters?.selected_sizes) {
      setSelectedSizes(user.filters.selected_sizes);
    }
    setLocalFilterType(user.filters?.type || "All");
    setLocalFilterInternational(!!user.filters?.international_only);
    setLocalFilterLocationType(user.filters?.location ? "Specified" : "None");
    setLocalFilterSizes((user.filters?.selected_sizes?.length ?? 0) > 0 ? "Selected" : "All");
  }, [user]);

  const [addedTitles, setAddedTitles] = useState([]);
  const [titleHover, setTitleHover] = useState({ rec: null, type: null });
  const hoverTimerRef = useRef(null);

  const addRecRef = useRef(null);

  const advertisePremium = () => {
    setPremiumPopup(true);
  };

  const sizeOptions = [
      "1-50",
      "51-1000",
      "1001-10,000",
      "10,000"
  ];

  const normalize = (s) => s.trim().replace(/\s+/g, " ").toLowerCase();

  const updateFilters = async (parameter, value) => {
    setFirstWait(false);
    setFiltersDisabled(true);

    try {
      const response = await fetch(`/api/update_filters?parameter=${parameter}&value=${value}`);
      const data = await response.json();

      if (!response.ok) {
        if (data.error_code === 'session_expired') {
          window.location.href = '/';
          return;
        }
        setErrorMessage(data.error_message || 'Error updating filters. Please try again.');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 4000);
        return;
      }

      if (data.jobs) {
        refreshJobs(data.jobs, data.user, data.total_jobs);
        if (parameter === 'add_title' || parameter === 'remove_title') {
          onRefreshCompanies();
        }
      }
      if (data.user) {
        syncFromServerPayload(data);
      }
      if (data.error_message) {
        setErrorMessage(data.error_message);
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 4000);
      }
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage('Error updating filters. Please try again.');
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 4000);
    } finally {
      setFirstWait(true);
      setFiltersDisabled(false);
    }
  };

  const submitFilters = (event) => {
    event.preventDefault(); // Prevent form submission

    let filters = {};

    // Get Job Type
    filters["type"] = localFilterType;

    // Get Availability
    let availability = document.querySelector(
      'input[name="international_only"]:checked'
    ).value;
    filters["international_only"] = availability === "true";

    // Get Location
    let locationType = document.querySelector(
      'input[name="location_type"]:checked'
    ).value;
    if (locationType === "Specified") {
      if (!locationIsValid) {
        setErrorMessage(
          "Please choose a valid location from the suggestions list."
        );
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 4000);
        return;
      }

      let location = (currentLocation || "").trim();
      let radius = document.querySelector('input[name="radius"]').value.trim();

      filters["location"] = location || null;
      filters["radius"] = radius || 50;
    } else {
      filters["location"] = null;
      filters["radius"] = 50;
    }

    // Get Sizes
    let sizeType = document.querySelector(
      'input[name="sizes"]:checked'
    ).value;
    if (sizeType === "Selected") {
      filters["selected_sizes"] = selectedSizes;
    } else {
      filters["selected_sizes"] = [];
    }

    // Optimistically update the UI to show the new filter buttons immediately
    setUser((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...filters,
      },
    }));

    // Call searchChange with 'filter_box' parameter and the filters object as value
    setFiltersBox(false);
    updateFilters("filter_box", JSON.stringify(filters));
  };

  const syncFromServerPayload = (payload) => {
    const srvUser = payload?.user;
    const srvDistinct = payload?.distinct_titles || [];

    // 1) Update our local copy of distinct titles from server
    setServerDistinctTitles(srvDistinct);

    // 2) Replace user with server user, but keep the currently selected tab/filter
    setUser((prev) => {
      const keepSelectedFilter = prev?.filters?.selected_filter ?? "All";
      return {
        ...(srvUser || {}),
        filters: {
          ...(srvUser?.filters || {}),
          selected_filter: keepSelectedFilter,
        },
      };
    });

    // 3) Make the chips show *exactly* what the server says:
    //    - addedTitles are any selected titles that aren't in distinct_titles (custom ones)
    //    - clear deletedTitles because server is truth
    const selected = srvUser?.filters?.selected_titles || [];
    const extras = selected.filter((t) => !srvDistinct.includes(t));
    setAddedTitles(extras);
    setDeletedTitles([]);
  };

  const resumeLabel = (
    <span className="resume-chip-text">
      <strong>Resume:</strong> '{user.resume_file || "No resume entered"}'
    </span>
  );

  const PencilIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="resume-chip-icon" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );

  const resumeChip = user.plan === "premium" ? (
    <button className="resume-chip" onClick={() => navigate("/index?noredir=1")}>
      {resumeLabel}
      <PencilIcon />
      <span className="resume-chip-tooltip">Click to upload a new resume</span>
    </button>
  ) : (
    <span className="resume-chip resume-chip-disabled">
      {resumeLabel}
      <span className="resume-chip-tooltip">Upgrade plan to upload a new resume</span>
    </span>
  );

  return (
    <>
      {user.filters && user.filters["selected_filter"] === "All" && (
        <>
          {selectedJobType === "internships" && (
            <div className="titles-heading-row">
              <h2>
                Internships Search Titles: (Click to filter)
                <TitleInfoTooltip
                  pinned={titleTooltipPinned}
                  hovered={titleTooltipHovered}
                  visible={titleTooltipVisible}
                  onPin={() => setTitleTooltipPinned((p) => !p)}
                  onHoverEnter={() => setTitleTooltipHovered(true)}
                  onHoverLeave={() => setTitleTooltipHovered(false)}
                  selectedTitles={user.filters?.selected_titles || []}
                />
              </h2>
              {resumeChip}
            </div>
          )}
          {selectedJobType === "new_grad" && (
            <div className="titles-heading-row">
              <h2>
                Job Search Titles: (Click to filter)
                <TitleInfoTooltip
                  pinned={titleTooltipPinned}
                  hovered={titleTooltipHovered}
                  visible={titleTooltipVisible}
                  onPin={() => setTitleTooltipPinned((p) => !p)}
                  onHoverEnter={() => setTitleTooltipHovered(true)}
                  onHoverLeave={() => setTitleTooltipHovered(false)}
                  selectedTitles={user.filters?.selected_titles || []}
                />
              </h2>
              {resumeChip}
            </div>
          )}
          <form
            id="filterForm"
            onSubmit={() => {
              refreshJobs();
            }}
          >
            <input type="hidden" name="parameter" value="title_filters" aria-label="Title filters parameter" />
          </form>

          <div className="filter-buttons" id="filter-buttons">
            {[...new Set([...serverDistinctTitles, ...addedTitles])]
              .sort((a, b) => a.localeCompare(b)) // stable order
              .map((rec) => {
                return deletedTitles.includes(rec) ? (
                  <></>
                ) : (
                  <button
                    key={rec}
                    disabled={filtersDisabled}
                    style={{
                      cursor: filtersDisabled ? "not-allowed" : "pointer",
                    }}
                    onMouseEnter={() => {
                      clearTimeout(hoverTimerRef.current);
                      hoverTimerRef.current = setTimeout(
                        () => setTitleHover({ rec, type: "button" }),
                        1000
                      );
                    }}
                    onMouseLeave={() => {
                      clearTimeout(hoverTimerRef.current);
                      setTitleHover({ rec: null, type: null });
                    }}
                    onClick={() => {
                      if (deletedTitles.includes(rec)) return;
                      //Frontend toggle
                      setUser((prev) => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          selected_titles: user.filters[
                            "selected_titles"
                          ].includes(rec)
                            ? prev.filters["selected_titles"].filter(
                                (val) => val !== rec
                              )
                            : [...prev.filters["selected_titles"], rec],
                        },
                      }));
                      //Backend toggle
                      updateFilters("title_filters", rec);
                    }}
                    data-rec={rec}
                    className={
                      user.filters &&
                      user.filters["selected_titles"].includes(rec)
                        ? "selected"
                        : ""
                    }
                  >
                    {rec}
                    {titleHover.rec === rec && titleHover.type === "button" && (
                      <div className="title-btn-tooltip">
                        <strong>'{rec}'</strong>{" "}
                        {user.filters["selected_titles"].includes(rec)
                          ? "Selected"
                          : "Unselected"}
                        . Click to toggle.
                        {user.plan === "premium" &&
                          " Click the 'X' in the top right to remove this search title."}
                      </div>
                    )}
                    {user.plan === "premium" && (
                      <span
                        className="close"
                        role="button"
                        tabIndex={filtersDisabled ? -1 : 0}
                        aria-label={`Remove ${rec} from search titles`}
                        style={{ zIndex: 9999, cursor: filtersDisabled ? "not-allowed" : "pointer" }}
                        onMouseEnter={() => {
                          clearTimeout(hoverTimerRef.current);
                          hoverTimerRef.current = setTimeout(
                            () => setTitleHover({ rec, type: "close" }),
                            1000
                          );
                        }}
                        onMouseLeave={() => {
                          clearTimeout(hoverTimerRef.current);
                          setTitleHover({ rec: null, type: null });
                          hoverTimerRef.current = setTimeout(
                            () => setTitleHover({ rec, type: "button" }),
                            1000
                          );
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (filtersDisabled) return;
                          //Frontend delete
                          setDeletedTitles((prev) => [...prev, rec]);
                          setTimeout(() => {
                            setUser((prev) => ({
                              ...prev,
                              filters: {
                                ...prev.filters,
                                selected_titles: prev.filters[
                                  "selected_titles"
                                ].filter((val) => val !== rec),
                              },
                            }));
                          }, 10);
                          //Backend delete
                          updateFilters("remove_title", rec);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            if (filtersDisabled) return;
                            setDeletedTitles((prev) => [...prev, rec]);
                            setTimeout(() => {
                              setUser((prev) => ({
                                ...prev,
                                filters: {
                                  ...prev.filters,
                                  selected_titles: prev.filters[
                                    "selected_titles"
                                  ].filter((val) => val !== rec),
                                },
                              }));
                            }, 10);
                            updateFilters("remove_title", rec);
                          }
                        }}
                      >
                        &times;
                        {titleHover.rec === rec &&
                          titleHover.type === "close" && (
                            <div className="close-btn-tooltip">
                              Remove this search term
                            </div>
                          )}
                      </span>
                    )}
                  </button>
                );
              })}
            <div className="add-rec-container">
              <label
                style={{
                  cursor: user.plan === "premium" ? "pointer" : "default",
                  position: "relative",
                }}
              >
                <button
                  className="add-rec-button"
                  disabled={user.plan !== "premium" || filtersDisabled}
                  style={{
                    cursor:
                      user.plan !== "premium" || filtersDisabled
                        ? "not-allowed"
                        : "pointer",
                    color: user.plan !== "premium" ? 1 : 0.5,
                  }}
                  onClick={() => {
                    if (user.plan === "premium" && !filtersDisabled) {
                      setAddRec((prev) => !prev);
                    } else if (!filtersDisabled) {
                      advertisePremium(); // Trigger upgrade modal or similar
                    }
                  }}
                >
                  +
                </button>
                <div className="tooltip">
                  {user.plan === "premium"
                    ? "Add a search title"
                    : "Upgrade to premium to fully customize your search titles"}
                </div>
              </label>

              <div
                style={{
                  display: addRec ? "flex" : "none",
                  flexDirection: "row",
                }}
              >
                <div
                  className="add-rec-input-container"
                  style={{ display: "flex" }}
                >
                  <input
                    type="text"
                    aria-label="Enter a new search title"
                    id="addRecInput"
                    className="add-rec-input"
                    placeholder="Search for"
                    ref={addRecRef}
                    minLength="3"
                    required
                  />
                  <span className="intern-label" id="internLabel">
                    {" "}
                    Intern
                  </span>
                </div>
                <button
                  type="button"
                  disabled={filtersDisabled}
                  style={{
                    cursor: filtersDisabled ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    const raw = addRecRef.current?.value ?? "";
                    const base = raw.trim();
                    if (!base) return;

                    const newTitle = `${base} Intern`;
                    const exists = (serverDistinctTitles || []).some(
                      (t) => normalize(t) === normalize(newTitle)
                    );

                    if (!exists) {
                      setAddedTitles((prev) => [...prev, newTitle]);
                      setDeletedTitles((prev) =>
                        prev.filter((t) => normalize(t) !== normalize(newTitle))
                      );
                      updateFilters("add_title", newTitle);
                    }

                    addRecRef.current.value = "";
                    setAddRec(false);
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          <hr className="divider" />
        </>
      )}

      {user.filters && user.filters["selected_filter"] === "All" && (
        <>

          {/* Company Search Section */}
          <CompanySearch
            user={user}
            setUser={setUser}
            selectedCompanyIds={selectedCompanyIds}
            setSelectedCompanyIds={setSelectedCompanyIds}
            favoriteCompanyIds={favoriteCompanyIds}
            onToggleFavoriteCompany={onToggleFavoriteCompany}
            filtersDisabled={filtersDisabled}
            setErrorMessage={setErrorMessage}
            setShowErrorPopup={setShowErrorPopup}
            onLoadCompanyJobs={onLoadCompanyJobs}
            allCompanies={allCompanies}
            companiesLoading={companiesLoading}
          />

          <hr className="divider" />

          {
            <>
              <div className="filter-button-container">
                <button
                  className="filter-button"
                  id="filter-button"
                  disabled={filtersDisabled}
                  style={{
                    cursor: filtersDisabled ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    setFiltersBox((prev) => !prev);
                  }}
                >
                  Filters
                  <PencilIcon />
                </button>
                <div
                  className="filter-buttons"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginLeft: "4px",
                  }}
                >
                  <span>:</span>
                  {user.filters["type"] === "All" &&
                  user.filters["international_only"] === false &&
                  user.filters["location"] === null &&
                  (user.filters?.selected_sizes?.length ?? 0) === 0 ? (
                    <button
                      disabled={filtersDisabled}
                      onClick={() => {
                        setFiltersBox((prev) => !prev);
                      }}
                      style={{
                        cursor: filtersDisabled ? "not-allowed" : "pointer",
                        fontSize: "12px",
                        padding: "3px 8px",
                      }}
                    >
                      No filters selected
                    </button>
                  ) : (
                    <>
                      {user.filters["type"] !== "All" && (
                        <button
                          className="selected"
                          disabled={filtersDisabled}
                          onClick={() => {
                            setFiltersBox((prev) => !prev);
                          }}
                          style={{
                            cursor: filtersDisabled ? "not-allowed" : "pointer",
                            fontSize: "12px",
                            padding: "3px 8px",
                          }}
                        >
                          Job Type: {user.filters["type"]}
                        </button>
                      )}
                      {user.filters["international_only"] === true && (
                        <button
                          className="selected"
                          disabled={filtersDisabled}
                          onClick={() => {
                            setFiltersBox((prev) => !prev);
                          }}
                          style={{
                            cursor: filtersDisabled ? "not-allowed" : "pointer",
                            fontSize: "12px",
                            padding: "3px 8px",
                          }}
                        >
                          Availability: H1 Sponsorship Likely
                        </button>
                      )}
                      {user.filters["location"] !== null && (
                        <button
                          className="selected"
                          disabled={filtersDisabled}
                          onClick={() => {
                            setFiltersBox((prev) => !prev);
                          }}
                          style={{
                            cursor: filtersDisabled ? "not-allowed" : "pointer",
                            fontSize: "12px",
                            padding: "3px 8px",
                          }}
                        >
                          Location: {user.filters["location"]}, within{" "}
                          {user.filters["radius"]} miles
                        </button>
                      )}
                      {user.filters["selected_sizes"] !== null &&
                        (user.filters?.selected_sizes?.length ?? 0) > 0 && (
                          <div className="dropdown">
                            <button className="dropdown-button">
                              {`Company Size: ${user.filters["selected_sizes"].length} selected...`}
                            </button>
                            <div
                              className="dropdown-aligner"
                              style={{ position: "relative" }}
                            >
                              <div
                                className="dropdown-content"
                                style={{
                                  top: "-5px",
                                  left: "5px",
                                  cursor: "default",
                                  fontSize: "12px",
                                  fontFamily: "Roboto, sans-serif",
                                }}
                              >
                                {user.filters["selected_sizes"].map(
                                  (size) => (
                                    <button
                                      className="selected-size-button"
                                      onClick={() => {}}
                                    >
                                      {size === "10,000" ? "10,000+" : size}
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>

              <div
                id="filterBox"
                className="filter-box"
                style={{ display: filtersBox ? "block" : "none" }}
              >
                <form
                  id="filtersForm"
                  onSubmit={(event) => {
                    submitFilters(event);
                  }}
                >
                  <input type="hidden" name="parameter" value="filter_box" />

                  <div className="filter-fields-container">
                    <div className="filter-field filter-field-job-type">
                      <h3>Location Type:</h3>
                      <label>
                        <input
                          type="radio"
                          name="type"
                          value="All"
                          checked={localFilterType === "All"}
                          tabIndex={localFilterType === "All" ? -1 : 0}
                          onChange={() => setLocalFilterType("All")}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterType("All"); } }}
                        />{" "}
                        All Jobs
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="type_remote"
                          value="Remote"
                          checked={localFilterType === "Remote"}
                          tabIndex={localFilterType === "Remote" ? -1 : 0}
                          onChange={() => setLocalFilterType("Remote")}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterType("Remote"); } }}
                        />{" "}
                        Remote Jobs
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="type_inperson"
                          value="In-person"
                          checked={localFilterType === "In-person"}
                          tabIndex={localFilterType === "In-person" ? -1 : 0}
                          onChange={() => setLocalFilterType("In-person")}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterType("In-person"); } }}
                        />{" "}
                        In-Person Jobs
                      </label>
                    </div>

                    <div className="filter-field filter-field-availability">
                      <h3>Sponsorship Likely:</h3>
                      <label>
                        <input
                          type="radio"
                          name="international_only"
                          value={false}
                          checked={!localFilterInternational}
                          tabIndex={!localFilterInternational ? -1 : 0}
                          onChange={() => setLocalFilterInternational(false)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterInternational(false); } }}
                        />{" "}
                        All Jobs
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="international_only"
                          value={true}
                          checked={localFilterInternational}
                          tabIndex={localFilterInternational ? -1 : 0}
                          onChange={() => setLocalFilterInternational(true)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterInternational(true); } }}
                        />{" "}
                        H1 Sponsorship Likely
                        <span
                          className="tooltip-container"
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              background: "#3b3b3b",
                              color: "white",
                              fontSize: "9px",
                              lineHeight: "12px",
                              textAlign: "center",
                              cursor: "pointer",
                              marginLeft: "3px",
                            }}
                          >
                            ?
                          </span>
                          <span
                            className="tooltip-text"
                            style={{
                              width: "200px",
                              backgroundColor: "#333",
                              color: "#fff",
                              textAlign: "left",
                              borderRadius: "6px",
                              padding: "6px",
                              position: "absolute",
                              zIndex: 1,
                              bottom: "125%",
                              left: "50%",
                              transform: "translateX(-50%)",
                              fontSize: "11px",
                            }}
                          >
                            This filter uses up to date LCA Disclosure data from
                            the U.S. Department of Labor to determine which
                            companies sponsor H1 Visas.
                          </span>
                        </span>
                      </label>
                    </div>

                    <div className="filter-field filter-field-location">
                      <h3>Location:</h3>
                      <label>
                        <input
                          type="radio"
                          name="location_type"
                          value="None"
                          checked={localFilterLocationType === "None"}
                          tabIndex={localFilterLocationType === "None" ? -1 : 0}
                          onChange={() => { setLocalFilterLocationType("None"); setLocationBox(false); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterLocationType("None"); setLocationBox(false); } }}
                        />{" "}
                        No Location
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="location_type"
                          value="Specified"
                          checked={localFilterLocationType === "Specified"}
                          tabIndex={localFilterLocationType === "Specified" ? -1 : 0}
                          onChange={() => { setLocalFilterLocationType("Specified"); setLocationBox(true); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterLocationType("Specified"); setLocationBox(true); } }}
                        />{" "}
                        Specify Location
                      </label>

                      <div
                        id="locationFields"
                        style={{ display: locationBox ? "block" : "none" }}
                      >
                        <div className="location-input-group">
                          <span>Location:</span>
                          <AutocompleteLocation
                            id="filters_location"
                            setLocation={setCurrentLocation}
                            setIsValid={setLocationIsValid}
                            overrideValue={
                              user.filters && user.filters["location"] !== ""
                                ? user.filters["location"]
                                : ""
                            }
                          />
                        </div>
                        <div className="location-input-group">
                          <span>Within:</span>
                          <input
                            type="number"
                            name="radius"
                            aria-label="Enter the radius for location search"
                            className="miles-input"
                            defaultValue={
                              user.filters && user.filters["radius"] !== ""
                                ? user.filters["radius"]
                                : 50
                            }
                            min="0"
                            step="1"
                          />
                          <span>miles</span>
                        </div>
                      </div>
                    </div>

                    <div className="filter-field filter-field-size">
                      <h3>Company Size:</h3>
                      <label>
                        <input
                          type="radio"
                          name="sizes"
                          value="All"
                          checked={localFilterSizes === "All"}
                          tabIndex={localFilterSizes === "All" ? -1 : 0}
                          onChange={() => { setLocalFilterSizes("All"); setSizesBox(false); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterSizes("All"); setSizesBox(false); } }}
                        />{" "}
                        All Sizes
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="sizes"
                          value="Selected"
                          checked={localFilterSizes === "Selected"}
                          tabIndex={localFilterSizes === "Selected" ? -1 : 0}
                          onChange={() => { setLocalFilterSizes("Selected"); setSizesBox(true); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setLocalFilterSizes("Selected"); setSizesBox(true); } }}
                        />{" "}
                        Select Company Size
                      </label>
                      <div
                        className="size-checkboxes"
                        id="sizeCheckboxes"
                        style={{ display: sizesBox ? "block" : "none" }}
                      >
                        {sizeOptions.map((size) => {
                          return (
                            <div className="size-label">
                              <input
                                type="checkbox"
                                name="selected_sizes"
                                aria-label={`Select company size: ${size}`}
                                value={size}
                                checked={selectedSizes.includes(size)}
                                onChange={(event) => {
                                  setSelectedSizes((prev) =>
                                    event.target.checked
                                      ? [...prev, event.target.value]
                                      : prev.filter(
                                          (val) => val !== event.target.value
                                        )
                                  );
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    setSelectedSizes((prev) =>
                                      prev.includes(size)
                                        ? prev.filter((val) => val !== size)
                                        : [...prev, size]
                                    );
                                  }
                                }}
                              />
                              {size === "10,000" ? "10,000+" : size}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="filter-apply-button"
                    disabled={filtersDisabled}
                    style={{
                      cursor: filtersDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    Apply Filters
                  </button>
                </form>
              </div>
            </>
          }

          <hr className="divider" />

          <div className="recommendations-header">
            <h2 className="recommendations-title">
              {selectedJobType === "internships" &&
                "Internship Recommendations:"}
              {selectedJobType === "new_grad" && "Job Recommendations:"}
            </h2>

            {/*
                  <span
                      className="filters-full-profile-link"
                      onClick={() => navigate("/profile")}
                      role="button"
                      tabIndex={0}
                    >
                      ➔ See How AI Scans Your Resume
                    </span>
                  */}
          </div>
        </>
      )}
      {user.filters && user.filters["selected_filter"] === "Favorites" && (
        <>
          <h1>Favorites:</h1>
          <FavoritesTabs
            activeTab={favoritesSubTab}
            setActiveTab={setFavoritesSubTab}
            favoriteCompanyIds={favoriteCompanyIds}
            favoriteCompanyInfo={favoriteCompanyInfo}
            onLoadFavoriteCompanyJobs={onLoadFavoriteCompanyJobs}
            onRemoveFavoriteCompany={onToggleFavoriteCompany}
            user={user}
          />
        </>
      )}
      {user.filters && user.filters["selected_filter"] === "Applied_to" && (
        <h1>Positions Applied To:</h1>
      )}
    </>
  );
}

export { FiltersSection };